'use client';

import Image from 'next/image';
import React, { useMemo } from 'react';
import { useBuilderArticles } from '@/hooks/useBuilderArticles';
import { BuilderArticlePageReference, ArticlePreview } from '@/types/article';
import { BuilderState } from '@/types/builder';
import { CarouselContainer, Breakpoints } from '../Carousel/Carousel';
import { host } from '@/config';

export const HUB_NEWS_CAROUSEL_NAME = 'HubNewsCarousel';
const HUB_NEWS_CAROUSEL_DEFAULT_LIMIT = 8;

interface HubNewsCarouselProps {
  title?: string;
  autoSelectLatest?: boolean;
  limit?: number;
  articles?: { article?: BuilderArticlePageReference }[];
  builderState: BuilderState;
}

const formatDate = (timestamp?: number): string | null => {
  if (!timestamp) return null;
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(timestamp));
};

const NewsCard: React.FC<{ article: ArticlePreview }> = ({ article }) => {
  const { title, heroImage, url, lastUpdated } = article;
  const date = useMemo(() => formatDate(lastUpdated), [lastUpdated]);

  return (
    <a
      href={`${host}${url}`}
      className="border-background-400 shadow-tc relative flex h-full min-h-[140px] flex-col justify-between overflow-hidden rounded-lg border p-4 md:min-h-[180px]"
    >
      {heroImage && (
        <Image src={heroImage} alt={title || ''} fill className="object-cover" sizes="(max-width: 768px) 50vw, 25vw" />
      )}
      <div className="pointer-events-none absolute inset-0 rounded-lg bg-linear-to-b from-transparent to-black" />

      <p className="text-neutral relative z-10 my-0 text-[18px] leading-[1.2] font-bold">{title}</p>

      {date && <p className="text-neutral relative z-10 my-0 text-[16px] leading-[1.4] font-semibold">{date}</p>}
    </a>
  );
};

export const HubNewsCarousel: React.FC<HubNewsCarouselProps> = ({
  title,
  autoSelectLatest = true,
  limit = HUB_NEWS_CAROUSEL_DEFAULT_LIMIT,
  articles: articleInputs = [],
  builderState,
}) => {
  const articles = useBuilderArticles({
    autoSelectLatest,
    limit,
    latestArticles: builderState.state.featuredArticles,
    articleInputs,
    categories: builderState.state.categories,
  });

  if (!articles.length) return null;

  return (
    <div className="flex flex-col gap-4">
      {title && <div className="tc-subheading my-0" dangerouslySetInnerHTML={{ __html: title }} />}

      <CarouselContainer
        useCarousel={articles.length >= 2}
        settings={{
          slidesToShow: 4,
          slidesToScroll: 1,
          showArrows: true,
          responsive: [
            {
              breakpoint: Breakpoints.lg,
              settings: { slidesToShow: 3, slidesToScroll: 1 },
            },
            {
              breakpoint: Breakpoints.md,
              settings: { slidesToShow: 2, slidesToScroll: 1 },
            },
          ],
        }}
      >
        {articles.map((article, index) => (
          <NewsCard key={index} article={article} />
        ))}
      </CarouselContainer>
    </div>
  );
};
